// Advanced Print Service - Professional Photo Booth Printing System
// Implements all requirements from the expert specification

class AdvancedPrintService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
    this.printQueue = [];
    this.isProcessing = false;
    this.supportedPrinters = new Map();
    this.printerCapabilities = new Map();
    this.defaultSettings = {
      size: '4x6',
      quality: 'standard',
      copies: 1,
      paperType: 'glossy',
      border: true,
      logo: true
    };
  }

  // Professional printer support - DNP, Mitsubishi, etc.
  getSupportedPrinters() {
    return {
      DNP: {
        models: ['DS620A', 'DS40', 'DS80', 'RX1HS'],
        features: ['auto-detection', 'high-speed', 'multiple-sizes'],
        sizes: ['4x6', '6x8', '8x10'],
        qualities: ['standard', 'high', 'ultra']
      },
      Mitsubishi: {
        models: ['CP-D80DW', 'CP-D90DW'],
        features: ['auto-detection', 'high-quality', 'durable'],
        sizes: ['4x6', '5x7', '6x8'],
        qualities: ['standard', 'high']
      },
      Epson: {
        models: ['SureColor', 'PictureMate'],
        features: ['wide-format', 'color-accuracy'],
        sizes: ['4x6', '5x7', '8x10', '11x14'],
        qualities: ['standard', 'high', 'ultra']
      },
      Canon: {
        models: ['SELPHY', 'imagePROGRAF'],
        features: ['compact', 'wireless'],
        sizes: ['4x6', '5x7', '6x8'],
        qualities: ['standard', 'high']
      }
    };
  }

  // Auto-detect printers
  async detectPrinters() {
    return new Promise((resolve, reject) => {
      this.send('detectPrinters');
      
      const timeout = setTimeout(() => {
        reject(new Error('Printer detection timeout'));
      }, 10000);

      const onPrinters = (printers) => {
        clearTimeout(timeout);
        this.off('printers', onPrinters);
        
        // Categorize detected printers
        const categorized = {
          thermal: [],
          inkjet: [],
          laser: [],
          supported: [],
          unsupported: []
        };

        printers.forEach(printer => {
          const supported = this.isPrinterSupported(printer);
          if (supported) {
            categorized.supported.push(printer);
            categorized.thermal.push(printer); // Most photo booths use thermal
          } else {
            categorized.unsupported.push(printer);
          }
        });

        resolve(categorized);
      };

      this.on('printers', onPrinters);
    });
  }

  // Check if printer is supported
  isPrinterSupported(printerInfo) {
    const supported = this.getSupportedPrinters();
    
    for (const [brand, info] of Object.entries(supported)) {
      if (printerInfo.name.toLowerCase().includes(brand.toLowerCase()) ||
          info.models.some(model => printerInfo.name.toLowerCase().includes(model.toLowerCase()))) {
        return true;
      }
    }
    
    return false;
  }

  // Get printer capabilities
  async getPrinterCapabilities(printerName) {
    return new Promise((resolve, reject) => {
      this.send('getPrinterCapabilities', { printerName });
      
      const timeout = setTimeout(() => {
        reject(new Error('Capability detection timeout'));
      }, 5000);

      const onCapabilities = (data) => {
        clearTimeout(timeout);
        this.off('printerCapabilities', onCapabilities);
        this.printerCapabilities.set(printerName, data);
        resolve(data);
      };

      this.on('printerCapabilities', onCapabilities);
    });
  }

  // Professional print sizes (inches and cm)
  getPrintSizes() {
    return {
      '4x6': { inches: '4x6', cm: '10x15', aspect: '2:3' },
      '5x7': { inches: '5x7', cm: '13x18', aspect: '5:7' },
      '6x8': { inches: '6x8', cm: '15x20', aspect: '3:4' },
      '8x10': { inches: '8x10', cm: '20x25', aspect: '4:5' },
      '6x4': { inches: '6x4', cm: '15x10', aspect: '3:2' }
    };
  }

  // Enhanced print with professional options
  printImage(imageData, options = {}) {
    const printJob = {
      id: Date.now().toString(),
      imageData,
      timestamp: new Date().toISOString(),
      status: 'queued',
      options: {
        // Basic options
        printerName: options.printerName || 'default',
        copies: options.copies || this.defaultSettings.copies,
        size: options.size || this.defaultSettings.size,
        quality: options.quality || this.defaultSettings.quality,
        
        // Professional options
        paperType: options.paperType || this.defaultSettings.paperType,
        border: options.border !== undefined ? options.border : this.defaultSettings.border,
        logo: options.logo !== undefined ? options.logo : this.defaultSettings.logo,
        
        // Advanced options
        colorCorrection: options.colorCorrection || 'auto',
        sharpening: options.sharpening || 'medium',
        brightness: options.brightness || 0,
        contrast: options.contrast || 0,
        
        // Layout options
        layout: options.layout || 'fit', // fit, fill, stretch
        orientation: options.orientation || 'landscape', // landscape, portrait
        
        // Branding options
        brandName: options.brandName || 'AI Photo Booth',
        brandLogo: options.brandLogo || null,
        
        // Metadata
        eventName: options.eventName || '',
        dateTaken: options.dateTaken || new Date().toISOString(),
        template: options.template || 'default'
      }
    };

    // Add to queue
    this.printQueue.push(printJob);
    this.send('print', printJob);
    this.emit('jobQueued', printJob);
    this.processQueue();
    
    return printJob.id;
  }

  // Enhanced queue management
  async processQueue() {
    if (this.isProcessing || this.printQueue.length === 0) return;
    
    this.isProcessing = true;
    this.emit('queueProcessing', { queue: this.printQueue });
    
    while (this.printQueue.length > 0) {
      const job = this.printQueue[0];
      job.status = 'processing';
      this.emit('jobProcessing', job);
      
      // Wait for job completion or timeout
      await new Promise((resolve) => {
        const timeout = setTimeout(() => {
          job.status = 'timeout';
          this.emit('jobTimeout', job);
          this.printQueue.shift();
          resolve();
        }, 60000); // 60 second timeout for professional printers
        
        const onStatus = (data) => {
          if (data.jobId === job.id) {
            if (data.status === 'completed') {
              clearTimeout(timeout);
              job.status = 'completed';
              job.completedAt = new Date().toISOString();
              this.printQueue.shift();
              this.emit('jobCompleted', job);
              resolve();
            } else if (data.status === 'failed') {
              clearTimeout(timeout);
              job.status = 'failed';
              job.error = data.error;
              this.emit('jobFailed', job);
              this.printQueue.shift();
              resolve();
            } else if (data.status === 'progress') {
              job.progress = data.progress;
              this.emit('jobProgress', job);
            }
          }
        };
        
        this.on('printStatus', onStatus);
      });
    }
    
    this.isProcessing = false;
    this.emit('queueCompleted');
  }

  // Retry failed print
  retryJob(jobId) {
    const failedJob = this.printQueue.find(job => job.id === jobId);
    if (failedJob && failedJob.status === 'failed') {
      // Reset job status and add to front of queue
      failedJob.status = 'queued';
      failedJob.error = null;
      failedJob.progress = 0;
      this.printQueue = [failedJob, ...this.printQueue.filter(job => job.id !== jobId)];
      this.processQueue();
      return true;
    }
    return false;
  }

  // Enhanced queue status with detailed info
  getQueueStatus() {
    const categorized = {
      queued: [],
      processing: [],
      completed: [],
      failed: [],
      timeout: []
    };

    this.printQueue.forEach(job => {
      if (job.status && categorized[job.status]) {
        categorized[job.status].push(job);
      }
    });

    return {
      queue: this.printQueue,
      categorized,
      isProcessing: this.isProcessing,
      total: this.printQueue.length,
      byStatus: Object.keys(categorized).reduce((acc, status) => {
        acc[status] = categorized[status].length;
        return acc;
      }, {})
    };
  }

  // Print with preview
  async printWithPreview(imageData, options = {}) {
    // Generate preview
    const preview = await this.generatePreview(imageData, options);
    
    return new Promise((resolve, reject) => {
      // Show preview to user
      this.emit('previewGenerated', { jobId: null, preview, options });
      
      // Wait for user confirmation
      const onConfirm = (confirmed) => {
        this.off('printConfirm', onConfirm);
        
        if (confirmed) {
          const jobId = this.printImage(imageData, options);
          resolve(jobId);
        } else {
          reject(new Error('Print cancelled by user'));
        }
      };
      
      this.on('printConfirm', onConfirm);
    });
  }

  // Generate print preview
  async generatePreview(imageData, options) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size based on print size
    const size = this.getPrintSizes()[options.size || '4x6'];
    const dpi = 300; // Print quality DPI
    canvas.width = parseInt(size.inches.split('x')[0]) * dpi;
    canvas.height = parseInt(size.inches.split('x')[1]) * dpi;
    
    // Load and draw image
    const img = new Image();
    img.onload = () => {
      // Apply layout and effects
      this.applyPrintLayout(ctx, img, canvas, options);
      
      // Convert to data URL
      const preview = canvas.toDataURL('image/jpeg', 0.9);
      return preview;
    };
    
    img.src = imageData;
  }

  // Apply professional print layout
  applyPrintLayout(ctx, img, canvas, options) {
    // Clear canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Calculate image dimensions
    let { x, y, width, height } = this.calculateImageDimensions(img, canvas, options.layout);
    
    // Apply filters
    ctx.filter = this.buildFilterString(options);
    
    // Draw main image
    ctx.drawImage(img, x, y, width, height);
    
    // Add border if requested
    if (options.border) {
      this.addBorder(ctx, canvas, options);
    }
    
    // Add logo/branding if requested
    if (options.logo && options.brandLogo) {
      this.addBranding(ctx, canvas, options);
    }
    
    // Add metadata
    if (options.eventName || options.dateTaken) {
      this.addMetadata(ctx, canvas, options);
    }
  }

  // Calculate image dimensions for different layouts
  calculateImageDimensions(img, canvas, layout) {
    const canvasRatio = canvas.width / canvas.height;
    const imgRatio = img.width / img.height;
    
    let x, y, width, height;
    
    switch (layout) {
      case 'fill':
        // Fill entire canvas (may crop)
        if (imgRatio > canvasRatio) {
          height = canvas.height;
          width = height * imgRatio;
          x = (canvas.width - width) / 2;
          y = 0;
        } else {
          width = canvas.width;
          height = width / imgRatio;
          x = 0;
          y = (canvas.height - height) / 2;
        }
        break;
        
      case 'stretch':
        // Stretch to fill (may distort)
        x = 0;
        y = 0;
        width = canvas.width;
        height = canvas.height;
        break;
        
      default: // 'fit'
        // Fit within canvas (may have empty space)
        if (imgRatio > canvasRatio) {
          width = canvas.width;
          height = width / imgRatio;
          x = 0;
          y = (canvas.height - height) / 2;
        } else {
          height = canvas.height;
          width = height * imgRatio;
          x = (canvas.width - width) / 2;
          y = 0;
        }
    }
    
    return { x, y, width, height };
  }

  // Build CSS filter string
  buildFilterString(options) {
    const filters = [];
    
    if (options.brightness) {
      filters.push(`brightness(${100 + options.brightness}%)`);
    }
    
    if (options.contrast) {
      filters.push(`contrast(${100 + options.contrast}%)`);
    }
    
    if (options.sharpening) {
      const sharpeningMap = {
        'low': 'contrast(1.1)',
        'medium': 'contrast(1.2)',
        'high': 'contrast(1.3)'
      };
      filters.push(sharpeningMap[options.sharpening] || '');
    }
    
    return filters.join(' ');
  }

  // Add professional border
  addBorder(ctx, canvas, options) {
    ctx.strokeStyle = options.borderColor || '#ffffff';
    ctx.lineWidth = options.borderWidth || 10;
    ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
  }

  // Add branding/logo
  addBranding(ctx, canvas, options) {
    const logoSize = 50;
    const margin = 20;
    
    // Draw logo in bottom right
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fillRect(canvas.width - logoSize - margin, canvas.height - logoSize - margin, logoSize, logoSize);
    
    // Add brand name
    ctx.fillStyle = '#333333';
    ctx.font = '12px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(options.brandName || 'AI Photo Booth', canvas.width - margin, canvas.height - 10);
  }

  // Add metadata (date, event name)
  addMetadata(ctx, canvas, options) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(10, canvas.height - 40, 300, 30);
    
    ctx.fillStyle = '#333333';
    ctx.font = '14px Arial';
    ctx.textAlign = 'left';
    
    const date = new Date(options.dateTaken).toLocaleDateString();
    const text = options.eventName ? `${options.eventName} - ${date}` : date;
    
    ctx.fillText(text, 20, canvas.height - 20);
  }

  // Enhanced error handling
  handlePrintError(error, jobId) {
    const errorTypes = {
      'NO_PAPER': {
        message: 'No paper in printer',
        action: 'load_paper',
        recoverable: true
      },
      'PAPER_JAM': {
        message: 'Paper jam',
        action: 'clear_jam',
        recoverable: true
      },
      'INK_LOW': {
        message: 'Ink/ribbon low',
        action: 'replace_ink',
        recoverable: true
      },
      'PRINTER_OFFLINE': {
        message: 'Printer offline',
        action: 'check_connection',
        recoverable: true
      },
      'UNKNOWN_ERROR': {
        message: 'Unknown printer error',
        action: 'restart_printer',
        recoverable: false
      }
    };

    const errorInfo = errorTypes[error.code] || errorTypes['UNKNOWN_ERROR'];
    
    this.emit('printError', {
      jobId,
      error: errorInfo,
      originalError: error,
      timestamp: new Date().toISOString()
    });

    return errorInfo;
  }

  // Connection and WebSocket methods (same as original)
  connect(serverUrl = 'ws://localhost:8080') {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(serverUrl);
        
        this.ws.onopen = () => {
          console.log('Connected to advanced print server');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.emit('connected');
          this.detectPrinters(); // Auto-detect on connect
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('Disconnected from print server');
          this.isConnected = false;
          this.emit('disconnected');
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.emit('error', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  handleMessage(data) {
    switch (data.type) {
      case 'printers':
        this.emit('printers', data.printers);
        break;
      case 'printerCapabilities':
        this.emit('printerCapabilities', data);
        break;
      case 'printStatus':
        this.emit('printStatus', data);
        break;
      case 'queueUpdate':
        this.emit('queueUpdate', data);
        break;
      case 'connectionStatus':
        this.emit('connectionStatus', data);
        break;
      case 'error':
        this.handlePrintError(data.error, data.jobId);
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  send(type, data = {}) {
    if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, ...data }));
      return true;
    }
    return false;
  }

  // Event listeners
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect().catch(error => {
          console.error('Reconnection failed:', error);
        });
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
      this.emit('reconnectFailed');
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.reconnectAttempts = 0;
  }

  isReady() {
    return this.isConnected && this.ws.readyState === WebSocket.OPEN;
  }
}

// Create singleton instance
const advancedPrintService = new AdvancedPrintService();

export default advancedPrintService;
